import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ScrollArea } from './ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  BarChart3, Users, Clock, GraduationCap, Shield, TrendingUp,
  AlertTriangle, CheckCircle, Calendar, Download, RefreshCw,
  ChevronUp, ChevronDown, Minus, FileText, Activity
} from 'lucide-react';

export default function ReportingDashboard() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState('month');
  
  // Report data
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [shiftAnalytics, setShiftAnalytics] = useState(null);
  const [trainingCompliance, setTrainingCompliance] = useState(null);
  const [employeePerformance, setEmployeePerformance] = useState(null);
  const [complianceOverview, setComplianceOverview] = useState(null);

  useEffect(() => {
    fetchAllReports();
  }, [period]);

  const fetchAllReports = async () => {
    setLoading(true);
    try {
      const [summary, shifts, training, performance, compliance] = await Promise.all([
        api.get('/reports/dashboard-summary').catch(() => ({ data: null })),
        api.get(`/reports/shift-analytics?period=${period}`).catch(() => ({ data: null })),
        api.get('/reports/training-compliance').catch(() => ({ data: null })),
        api.get(`/reports/employee-performance?period=${period}`).catch(() => ({ data: null })),
        api.get('/reports/compliance-overview').catch(() => ({ data: null }))
      ]);

      setDashboardSummary(summary.data);
      setShiftAnalytics(shifts.data);
      setTrainingCompliance(training.data);
      setEmployeePerformance(performance.data);
      setComplianceOverview(compliance.data);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllReports();
    setRefreshing(false);
    toast.success('Reports refreshed');
  };

  const exportReport = (reportName) => {
    // Create CSV export
    let data = [];
    let headers = [];
    
    switch (reportName) {
      case 'employee-performance':
        headers = ['Name', 'Role', 'Shifts Assigned', 'Shifts Completed', 'Completion Rate', 'Active Certifications'];
        data = employeePerformance?.employees?.map(e => [
          e.employee_name, e.role, e.shifts_assigned, e.shifts_completed, 
          `${e.completion_rate}%`, e.active_certifications
        ]) || [];
        break;
      case 'training-compliance':
        headers = ['Training', 'Category', 'Valid Completions', 'Expiring Soon', 'Expired', 'Compliance Rate'];
        data = trainingCompliance?.training_overview?.map(t => [
          t.training_title, t.category, t.valid_completions,
          t.expiring_within_30_days, t.expired, `${t.compliance_rate}%`
        ]) || [];
        break;
      default:
        return;
    }

    const csvContent = [headers.join(','), ...data.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportName}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report exported');
  };

  const getTrendIcon = (value) => {
    if (value > 0) return <ChevronUp className="w-4 h-4 text-green-500" />;
    if (value < 0) return <ChevronDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'current':
        return <Badge className="bg-green-100 text-green-800">Current</Badge>;
      case 'warning':
        return <Badge className="bg-amber-100 text-amber-800">Warning</Badge>;
      case 'expired':
        return <Badge className="bg-red-100 text-red-800">Expired</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="reporting-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="section-header mb-0">
          <h2 className="section-title">Reports & Analytics</h2>
          <p className="section-subtitle">Comprehensive insights into operations and compliance</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing} className="hover:bg-purple-50">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5 mb-8 bg-purple-50/50">
          <TabsTrigger value="overview" className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-purple-900 data-[state=active]:shadow-sm">
            <BarChart3 className="w-4 h-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="shifts" className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-purple-900 data-[state=active]:shadow-sm">
            <Clock className="w-4 h-4" />
            Shifts
          </TabsTrigger>
          <TabsTrigger value="training" className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-purple-900 data-[state=active]:shadow-sm">
            <GraduationCap className="w-4 h-4" />
            Training
          </TabsTrigger>
          <TabsTrigger value="employees" className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-purple-900 data-[state=active]:shadow-sm">
            <Users className="w-4 h-4" />
            Performance
          </TabsTrigger>
          <TabsTrigger value="compliance" className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-purple-900 data-[state=active]:shadow-sm">
            <Shield className="w-4 h-4" />
            Compliance
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {dashboardSummary && (
            <>
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                <div className="card-stat">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 font-medium">Total Employees</p>
                      <p className="text-3xl font-bold text-purple-900 tracking-tight">{dashboardSummary.employees.total}</p>
                    </div>
                    <div className="icon-container">
                      <Users className="w-5 h-5 text-purple-600" />
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-3 text-sm">
                    <span className="text-gray-400">Caregivers: {dashboardSummary.employees.caregivers}</span>
                    <span className="text-gray-400">LPN: {dashboardSummary.employees.lpn}</span>
                  </div>
                </div>

                <div className="card-stat">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 font-medium">Active Clients</p>
                      <p className="text-3xl font-bold text-purple-900 tracking-tight">{dashboardSummary.clients.active}</p>
                    </div>
                    <div className="icon-container bg-gradient-to-br from-green-100 to-emerald-100 border-green-200">
                      <Activity className="w-5 h-5 text-green-600" />
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-3 text-sm">
                    <span className="text-gray-400">Total: {dashboardSummary.clients.total}</span>
                    <span className="text-gray-400">Pending: {dashboardSummary.clients.pending}</span>
                  </div>
                </div>

                <div className="card-stat">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 font-medium">Shifts This Week</p>
                      <p className="text-3xl font-bold text-purple-900 tracking-tight">{dashboardSummary.shifts_this_week.total}</p>
                    </div>
                    <div className="icon-container bg-gradient-to-br from-blue-100 to-sky-100 border-blue-200">
                      <Calendar className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-3 text-sm">
                    <span className="text-green-600 font-medium">{dashboardSummary.shifts_this_week.completed} completed</span>
                    <span className="text-amber-600 font-medium">{dashboardSummary.shifts_this_week.pending_review} pending</span>
                  </div>
                </div>

                <div className="card-stat">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 font-medium">Compliance Issues</p>
                      <p className="text-3xl font-bold text-purple-900 tracking-tight">
                        {dashboardSummary.compliance.expired + dashboardSummary.compliance.warning}
                      </p>
                    </div>
                    <div className={`icon-container ${dashboardSummary.compliance.expired > 0 ? 'bg-gradient-to-br from-red-100 to-rose-100 border-red-200' : 'bg-gradient-to-br from-green-100 to-emerald-100 border-green-200'}`}>
                      <Shield className={`w-5 h-5 ${dashboardSummary.compliance.expired > 0 ? 'text-red-600' : 'text-green-600'}`} />
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-3 text-sm">
                    <span className="text-red-600 font-medium">{dashboardSummary.compliance.expired} expired</span>
                    <span className="text-amber-600 font-medium">{dashboardSummary.compliance.warning} warnings</span>
                  </div>
                </div>
              </div>

              {/* Training Overview */}
              <div className="card-premium p-6">
                <h3 className="text-lg font-semibold text-purple-900 mb-4">Training Certifications</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <div>
                      <p className="font-semibold text-purple-900">{dashboardSummary.training.active_certifications}</p>
                      <p className="text-sm text-gray-500">Active Certifications</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    <div>
                      <p className="font-semibold text-purple-900">{dashboardSummary.training.expiring_within_30_days}</p>
                      <p className="text-sm text-gray-500">Expiring in 30 Days</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {/* Shifts Tab */}
        <TabsContent value="shifts" className="space-y-6">
          {shiftAnalytics && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Shifts by Status */}
                <Card className="p-6 border-purple-100">
                  <h3 className="text-lg font-semibold text-purple-900 mb-4">Shifts by Status</h3>
                  <div className="space-y-3">
                    {Object.entries(shiftAnalytics.shifts_by_status || {}).map(([status, count]) => (
                      <div key={status} className="flex items-center justify-between">
                        <span className="capitalize text-gray-700">{status.replace('_', ' ')}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-32 bg-gray-100 rounded-full h-2">
                            <div 
                              className="bg-purple-600 h-2 rounded-full"
                              style={{ width: `${Math.min((count / (shiftAnalytics.estimated_hours_worked / 8 || 1)) * 100, 100)}%` }}
                            />
                          </div>
                          <span className="font-medium w-12 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Shifts by Day of Week */}
                <Card className="p-6 border-purple-100">
                  <h3 className="text-lg font-semibold text-purple-900 mb-4">Shifts by Day</h3>
                  <div className="space-y-3">
                    {Object.entries(shiftAnalytics.shifts_by_day_of_week || {}).map(([day, count]) => (
                      <div key={day} className="flex items-center justify-between">
                        <span className="text-gray-700">{day}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-32 bg-gray-100 rounded-full h-2">
                            <div 
                              className="bg-blue-500 h-2 rounded-full"
                              style={{ width: `${Math.min((count / 20) * 100, 100)}%` }}
                            />
                          </div>
                          <span className="font-medium w-12 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>

              {/* Top Caregivers */}
              <Card className="p-6 border-purple-100">
                <h3 className="text-lg font-semibold text-purple-900 mb-4">Top Caregivers by Shifts</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rank</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="text-right">Completed Shifts</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(shiftAnalytics.top_caregivers || []).map((caregiver, index) => (
                      <TableRow key={caregiver.id}>
                        <TableCell>
                          <Badge variant={index < 3 ? 'default' : 'outline'} className={index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-amber-600' : ''}>
                            #{index + 1}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">{caregiver.name}</TableCell>
                        <TableCell className="text-right">{caregiver.shifts}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Training Tab */}
        <TabsContent value="training" className="space-y-6">
          {trainingCompliance && (
            <>
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold text-purple-900">Training Compliance Report</h3>
                  <p className="text-sm text-gray-600">
                    Overall compliance rate: <span className="font-bold text-purple-900">{trainingCompliance.overall_compliance_rate}%</span>
                  </p>
                </div>
                <Button variant="outline" onClick={() => exportReport('training-compliance')}>
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>

              {/* Training Overview Table */}
              <Card className="border-purple-100">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Training</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-center">Valid</TableHead>
                      <TableHead className="text-center">Expiring</TableHead>
                      <TableHead className="text-center">Expired</TableHead>
                      <TableHead className="text-center">Not Started</TableHead>
                      <TableHead className="text-right">Compliance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(trainingCompliance.training_overview || []).map((training) => (
                      <TableRow key={training.training_id}>
                        <TableCell className="font-medium">{training.training_title}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{training.category}</Badge>
                        </TableCell>
                        <TableCell className="text-center text-green-600">{training.valid_completions}</TableCell>
                        <TableCell className="text-center text-amber-600">{training.expiring_within_30_days}</TableCell>
                        <TableCell className="text-center text-red-600">{training.expired}</TableCell>
                        <TableCell className="text-center text-gray-500">{training.not_started}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Progress value={training.compliance_rate} className="w-16 h-2" />
                            <span className="font-medium">{training.compliance_rate}%</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>

              {/* Employees Needing Action */}
              {trainingCompliance.employees_needing_action?.length > 0 && (
                <Card className="p-6 border-amber-200 bg-amber-50">
                  <h3 className="text-lg font-semibold text-amber-900 mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Employees Needing Training Action ({trainingCompliance.employees_needing_action.length})
                  </h3>
                  <ScrollArea className="h-64">
                    <div className="space-y-3">
                      {trainingCompliance.employees_needing_action.map((employee) => (
                        <div key={employee.employee_id} className="bg-white p-3 rounded-lg border border-amber-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium">{employee.employee_name}</span>
                            <Badge variant="outline">{employee.role}</Badge>
                          </div>
                          {employee.missing_trainings.length > 0 && (
                            <div className="text-sm">
                              <span className="text-red-600 font-medium">Missing: </span>
                              {employee.missing_trainings.join(', ')}
                            </div>
                          )}
                          {employee.expiring_trainings.length > 0 && (
                            <div className="text-sm">
                              <span className="text-amber-600 font-medium">Expiring: </span>
                              {employee.expiring_trainings.map(t => t.title).join(', ')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Employee Performance Tab */}
        <TabsContent value="employees" className="space-y-6">
          {employeePerformance && (
            <>
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold text-purple-900">Employee Performance</h3>
                  <p className="text-sm text-gray-600">
                    Average completion rate: <span className="font-bold text-purple-900">{employeePerformance.summary?.avg_completion_rate || 0}%</span>
                  </p>
                </div>
                <Button variant="outline" onClick={() => exportReport('employee-performance')}>
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-4">
                <Card className="p-4 border-purple-100 text-center">
                  <p className="text-2xl font-bold text-purple-900">{employeePerformance.summary?.total_employees || 0}</p>
                  <p className="text-sm text-gray-600">Total Employees</p>
                </Card>
                <Card className="p-4 border-purple-100 text-center">
                  <p className="text-2xl font-bold text-green-600">{employeePerformance.summary?.total_shifts_completed || 0}</p>
                  <p className="text-sm text-gray-600">Shifts Completed</p>
                </Card>
                <Card className="p-4 border-purple-100 text-center">
                  <p className="text-2xl font-bold text-blue-600">{employeePerformance.summary?.total_trainings_completed || 0}</p>
                  <p className="text-sm text-gray-600">Trainings Completed</p>
                </Card>
                <Card className="p-4 border-purple-100 text-center">
                  <p className="text-2xl font-bold text-purple-600">{employeePerformance.summary?.avg_completion_rate || 0}%</p>
                  <p className="text-sm text-gray-600">Avg Completion Rate</p>
                </Card>
              </div>

              {/* Employee Table */}
              <Card className="border-purple-100">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead className="text-center">Assigned</TableHead>
                      <TableHead className="text-center">Completed</TableHead>
                      <TableHead className="text-center">Completion Rate</TableHead>
                      <TableHead className="text-center">Certifications</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(employeePerformance.employees || []).map((employee) => (
                      <TableRow key={employee.employee_id}>
                        <TableCell className="font-medium">{employee.employee_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{employee.role}</Badge>
                        </TableCell>
                        <TableCell className="text-center">{employee.shifts_assigned}</TableCell>
                        <TableCell className="text-center">{employee.shifts_completed}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Progress 
                              value={employee.completion_rate} 
                              className={`w-16 h-2 ${employee.completion_rate >= 90 ? '[&>div]:bg-green-500' : employee.completion_rate >= 70 ? '[&>div]:bg-amber-500' : '[&>div]:bg-red-500'}`}
                            />
                            <span className={`font-medium ${employee.completion_rate >= 90 ? 'text-green-600' : employee.completion_rate >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
                              {employee.completion_rate}%
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">{employee.active_certifications}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Compliance Tab */}
        <TabsContent value="compliance" className="space-y-6">
          {complianceOverview && (
            <>
              {/* Summary */}
              <div className="grid grid-cols-4 gap-4">
                <Card className="p-4 border-purple-100 text-center">
                  <p className="text-2xl font-bold text-purple-900">{complianceOverview.summary?.total_records || 0}</p>
                  <p className="text-sm text-gray-600">Total Records</p>
                </Card>
                <Card className="p-4 border-green-200 bg-green-50 text-center">
                  <p className="text-2xl font-bold text-green-600">{complianceOverview.summary?.overall_compliance_rate || 0}%</p>
                  <p className="text-sm text-gray-600">Overall Compliance</p>
                </Card>
                <Card className="p-4 border-amber-200 bg-amber-50 text-center">
                  <p className="text-2xl font-bold text-amber-600">{complianceOverview.summary?.items_expiring_soon || 0}</p>
                  <p className="text-sm text-gray-600">Expiring Soon</p>
                </Card>
                <Card className="p-4 border-red-200 bg-red-50 text-center">
                  <p className="text-2xl font-bold text-red-600">{complianceOverview.summary?.items_expired || 0}</p>
                  <p className="text-sm text-gray-600">Expired</p>
                </Card>
              </div>

              {/* By Record Type */}
              <Card className="p-6 border-purple-100">
                <h3 className="text-lg font-semibold text-purple-900 mb-4">Compliance by Record Type</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Record Type</TableHead>
                      <TableHead className="text-center">Total</TableHead>
                      <TableHead className="text-center">Current</TableHead>
                      <TableHead className="text-center">Warning</TableHead>
                      <TableHead className="text-center">Expired</TableHead>
                      <TableHead className="text-right">Compliance Rate</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(complianceOverview.by_record_type || []).map((record) => (
                      <TableRow key={record.record_type}>
                        <TableCell className="font-medium capitalize">{record.record_type.replace('_', ' ')}</TableCell>
                        <TableCell className="text-center">{record.total}</TableCell>
                        <TableCell className="text-center text-green-600">{record.current}</TableCell>
                        <TableCell className="text-center text-amber-600">{record.warning}</TableCell>
                        <TableCell className="text-center text-red-600">{record.expired}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Progress value={record.compliance_rate} className="w-16 h-2" />
                            <span className="font-medium">{record.compliance_rate}%</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>

              {/* Upcoming Expirations */}
              {complianceOverview.upcoming_expirations?.length > 0 && (
                <Card className="p-6 border-amber-200 bg-amber-50">
                  <h3 className="text-lg font-semibold text-amber-900 mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Upcoming Expirations (Next 30 Days)
                  </h3>
                  <ScrollArea className="h-48">
                    <div className="space-y-2">
                      {complianceOverview.upcoming_expirations.map((item) => (
                        <div key={item.id} className="bg-white p-3 rounded-lg border border-amber-200 flex items-center justify-between">
                          <div>
                            <span className="font-medium">{item.employee_name}</span>
                            <span className="text-gray-500 mx-2">-</span>
                            <span className="text-gray-700">{item.record_type}</span>
                          </div>
                          <Badge className="bg-amber-100 text-amber-800">
                            {item.days_until_expiration} days left
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </Card>
              )}

              {/* Expired Records */}
              {complianceOverview.expired_records?.length > 0 && (
                <Card className="p-6 border-red-200 bg-red-50">
                  <h3 className="text-lg font-semibold text-red-900 mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Expired Records Requiring Action
                  </h3>
                  <ScrollArea className="h-48">
                    <div className="space-y-2">
                      {complianceOverview.expired_records.map((item) => (
                        <div key={item.id} className="bg-white p-3 rounded-lg border border-red-200 flex items-center justify-between">
                          <div>
                            <span className="font-medium">{item.employee_name}</span>
                            <span className="text-gray-500 mx-2">-</span>
                            <span className="text-gray-700">{item.record_type}</span>
                          </div>
                          <Badge className="bg-red-100 text-red-800">
                            Expired {new Date(item.expiration_date).toLocaleDateString()}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
